/**
 * Your color. It is not decoration: it is how the house tells people apart —
 * the header initial, the chore cards, the calendar, the map pin. It lives in D1
 * (`family_members.color`), so this is config, and nothing hardcodes it.
 *
 * Big swatches, because this is the bit of the app a kid actually cares about.
 * Every swatch has a NAME as well as a hue (a swatch grid can't rely on color
 * alone), and the free hex field means the palette never becomes a cage.
 */
import { useId } from 'react';
import { Field, TextInput, cx } from './ui';

export const PALETTE: { name: string; hex: string }[] = [
  { name: 'Sage', hex: '#2f6f4e' },
  { name: 'Clay', hex: '#b4553f' },
  { name: 'Honey', hex: '#c98a3c' },
  { name: 'Dusk', hex: '#5b6ea8' },
  { name: 'Moss', hex: '#6d8f5e' },
  { name: 'Plum', hex: '#7d5a86' },
  { name: 'Teal', hex: '#2f6f6f' },
  { name: 'Berry', hex: '#a34a68' },
  { name: 'Sky', hex: '#3f7ea6' },
  { name: 'Slate', hex: '#6b7280' },
];

const HEX = /^#[0-9a-f]{6}$/i;

export function isHex(value: string): boolean {
  return HEX.test(value.trim());
}

export default function ColorField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (hex: string) => void;
  error?: string | null;
}) {
  const groupId = useId();
  const normalized = value.trim().toLowerCase();
  const current = PALETTE.find((p) => p.hex === normalized);

  return (
    <div className="flex flex-col gap-4">
      <div role="group" aria-labelledby={groupId} className="flex flex-col gap-2">
        <span id={groupId} className="text-sm font-medium text-ink">
          Pick your color
        </span>

        <div className="flex flex-wrap gap-2.5">
          {PALETTE.map((swatch) => {
            const selected = swatch.hex === normalized;
            return (
              <button
                key={swatch.hex}
                type="button"
                aria-pressed={selected}
                aria-label={swatch.name}
                title={swatch.name}
                onClick={() => onChange(swatch.hex)}
                className={cx(
                  'grid h-14 w-14 place-items-center rounded-full border-2 transition-transform',
                  'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none',
                  selected ? 'scale-105 border-ink' : 'border-border hover:scale-105'
                )}
                style={{ backgroundColor: swatch.hex }}
              >
                {/* The check is the second signal — the color is never the only one. */}
                {selected && (
                  <span aria-hidden="true" className="text-lg font-bold text-white drop-shadow">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-sm text-ink-soft">
          {isHex(value)
            ? `Your color is ${current ? current.name.toLowerCase() : normalized}.`
            : 'Pick one above, or type a hex code below.'}
        </p>
      </div>

      <Field label="Or type a hex code" error={error}>
        {(props) => (
          <TextInput
            {...props}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#2f6f4e"
            spellCheck={false}
            autoCapitalize="none"
            autoComplete="off"
            maxLength={7}
            className="max-w-40"
          />
        )}
      </Field>
    </div>
  );
}
